import { useState, useEffect, useContext } from 'react';
import { history } from 'umi';
import { useIntl } from 'react-intl';
import {
  AccountFilled,
  EmailOutlined,
  PhoneOutlined,
  IglooOutlined,
} from 'iglooicon';
import { Typography, message, Button } from 'iglooform';
import { useDispatch } from 'dva';
import './index.less';
import { useMemo } from 'react';
import { Radio, Menu, RadioChangeEvent, Spin } from 'antd';
import { Modal } from 'iglooform';
import logout from '@/pages/auth/logout';
import Context from '@/utils/context';
import copy from 'copy-to-clipboard';
import throttle from 'lodash/throttle';
import { getAgencyInfo } from './service';

interface IMe {
  menuClickCallback: () => void;
}

const MePage = ({ menuClickCallback }: IMe) => {
  const { formatMessage } = useIntl();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [loading, setLoading] = useState<boolean>(false);
  const { isPc } = useContext(Context);
  const dispatch = useDispatch();

  const handleGetAgencyInfo = throttle(
    async (): Promise<void> => {
      getAgencyInfo()
        .then((res) => {
          if (res) {
            setUserInfo(res.userInfo);
          }
        })
        .finally(() => setLoading(false));
    },
    1000,
    { leading: true, trailing: false },
  );

  useEffect(() => {
    setLoading(true);
    handleGetAgencyInfo();
  }, []);

  const onChange = (e: RadioChangeEvent) => {
    const currLang = e.target.value;
    localStorage.setItem(SELECTED_LANG, currLang); // 将选中的语言存入storage，以便在刷新页面的时候仍然保留选中的语言
    dispatch({
      type: 'global/setData',
      payload: {
        cursor: '0', // earnings commission需要重新请求
        currentLang: currLang,
      },
    });
    location.reload();
  };

  const handleLogout = () => {
    menuClickCallback && menuClickCallback();
    Modal.confirm({
      width: 700,
      title: formatMessage({ id: 'Notice' }),
      content: formatMessage({ id: 'Are you sure to logout?' }),
      okText: formatMessage({ id: 'Confirm' }),
      cancelText: formatMessage({ id: 'Cancel' }),
      mask: false,
      onOk: () => {
        logout();
        Modal.destroyAll();
      },
    });
  };

  const handleMenuClick = (path: string) => {
    // 更新顶部菜单导航条
    dispatch({
      type: 'global/setData',
      payload: {
        menuKey: '',
      },
    });
    menuClickCallback && menuClickCallback();
    history.push(path);
  };

  const userInfoRender = useMemo(() => {
    const emailSplit = userInfo?.email?.split('@');
    const email = emailSplit && emailSplit[0];
    const emailSuffix = emailSplit && emailSplit[1];
    if (!isPc) {
      return (
        userInfo && (
          <Spin spinning={loading}>
            <div className="userInfoBoxH5">
              <div className="firstBox">
                <div className="leftIcon">
                  <AccountFilled style={{ color: '#5858FF' }} />
                </div>
                <div className="rightInfo">
                  <div className="id">
                    <Typography
                      level="h3a"
                      style={{ fontSize: '18px', lineHeight: '24px' }}
                    >
                      {userInfo.name}
                    </Typography>
                  </div>
                  <div className="id">
                    <Typography
                      level="body2"
                      style={{ color: '#BDBDBD', marginRight: '12px' }}
                    >
                      ID
                    </Typography>
                    <Typography level="body2">{userInfo.display_id}</Typography>
                  </div>
                  {userInfo?.email && (
                    <div className="id">
                      <EmailOutlined
                        style={{ color: '#BDBDBD', marginRight: '8px' }}
                      />
                      <Typography level="body2">{`${email?.replace(
                        email?.substring(1, 3),
                        '*',
                      )}@${emailSuffix}`}</Typography>
                    </div>
                  )}
                  <div className="id">
                    <PhoneOutlined
                      style={{ color: '#BDBDBD', marginRight: '8px' }}
                    />
                    <Typography level="body2">{`+84 ****${userInfo.phone_number?.substring(
                      4,
                    )}`}</Typography>
                  </div>
                  {userInfo?.referral_code && (
                    <div className="id">
                      <IglooOutlined
                        style={{ color: '#BDBDBD', marginRight: '8px' }}
                      />
                      <Typography level="body2">
                        {userInfo?.referral_code}
                      </Typography>
                      <a
                        className="copy"
                        onClick={() => {
                          copy(userInfo?.referral_code);
                          message.success(
                            formatMessage({
                              id: `Referral Code is Copied!`,
                            }),
                          );
                        }}
                      >
                        <Typography level="body2">
                          {formatMessage({ id: 'Copy' })}
                        </Typography>
                      </a>
                    </div>
                  )}
                </div>
              </div>
              <Menu>
                <Menu.ItemGroup
                  title={
                    <Typography level="body1" style={{ color: '#212121' }}>
                      {formatMessage({ id: 'Business' })}
                    </Typography>
                  }
                >
                  <Menu.Item
                    key="0"
                    onClick={() => handleMenuClick('/me/business-management')}
                    style={{ marginBottom: '24px' }}
                  >
                    <section className="menu-item-container">
                      <Typography level="body1" style={{ color: '#666' }}>
                        {formatMessage({ id: 'Business Management' })}
                      </Typography>
                    </section>
                  </Menu.Item>
                </Menu.ItemGroup>
                <Menu.ItemGroup
                  title={
                    <Typography level="body1" style={{ color: '#212121' }}>
                      {formatMessage({ id: 'Payment' })}
                    </Typography>
                  }
                >
                  <Menu.Item
                    key="1"
                    onClick={() => handleMenuClick('/me/receiving-account')}
                    style={{ marginBottom: '24px' }}
                  >
                    <section className="menu-item-container">
                      <Typography level="body1" style={{ color: '#666' }}>
                        {formatMessage({ id: 'Default Receiving Account' })}
                      </Typography>
                    </section>
                  </Menu.Item>
                  <Menu.Item
                    key="2"
                    onClick={() =>
                      handleMenuClick(
                        '/me/verify-payment-password/change-payment-password',
                      )
                    }
                    style={{ marginBottom: '24px' }}
                  >
                    <section className="menu-item-container">
                      <Typography level="body1" style={{ color: '#666' }}>
                        {formatMessage({ id: 'Change Payment Password' })}
                      </Typography>
                    </section>
                  </Menu.Item>
                  <Menu.Item
                    key="8"
                    onClick={() =>
                      handleMenuClick('/me/forget-payment-password')
                    }
                    style={{ marginBottom: '24px' }}
                  >
                    <section className="menu-item-container">
                      <Typography level="body1" style={{ color: '#666' }}>
                        {formatMessage({ id: 'Forgot Payment Password' })}
                      </Typography>
                    </section>
                  </Menu.Item>
                </Menu.ItemGroup>
                <Menu.ItemGroup
                  title={
                    <Typography level="body1" style={{ color: '#212121' }}>
                      {formatMessage({ id: 'Password' })}
                    </Typography>
                  }
                >
                  <Menu.Item
                    key="3"
                    onClick={() => handleMenuClick('/me/change-password')}
                    style={{ marginBottom: '24px' }}
                  >
                    <section className="menu-item-container">
                      <Typography level="body1" style={{ color: '#666' }}>
                        {formatMessage({ id: 'Change Password' })}
                      </Typography>
                    </section>
                  </Menu.Item>
                  <Menu.Item
                    key="4"
                    onClick={() => handleMenuClick('/me/forget-password')}
                    style={{ marginBottom: '24px' }}
                  >
                    <section className="menu-item-container">
                      <Typography level="body1" style={{ color: '#666' }}>
                        {formatMessage({ id: 'Forgot Password' })}
                      </Typography>
                    </section>
                  </Menu.Item>
                </Menu.ItemGroup>
                <Menu.ItemGroup
                  title={
                    <Typography level="body1" style={{ color: '#212121' }}>
                      {formatMessage({ id: 'Language' })}
                    </Typography>
                  }
                >
                  <section className="lang-container">
                    <Radio.Group
                      onChange={onChange}
                      value={localStorage.getItem(SELECTED_LANG) || 'vi-VN'}
                      defaultValue="en-US"
                    >
                      <Radio
                        value="en-US"
                        checked={
                          localStorage.getItem(SELECTED_LANG) === 'en-US'
                        }
                      >
                        <Typography level={'body1'} style={{ color: '#666' }}>
                          English
                        </Typography>
                      </Radio>
                      <Radio
                        value="vi-VN"
                        checked={
                          localStorage.getItem(SELECTED_LANG) === 'vi-VN'
                        }
                      >
                        <Typography level={'body1'} style={{ color: '#666' }}>
                          Tiếng Việt
                        </Typography>
                      </Radio>
                    </Radio.Group>
                  </section>
                </Menu.ItemGroup>
              </Menu>
              <div style={{ marginTop: '24px' }}>
                <Button
                  type="primary"
                  style={{ width: '100%' }}
                  onClick={handleLogout}
                >
                  <Typography level="body1">
                    {formatMessage({ id: 'Logout' })}
                  </Typography>
                </Button>
              </div>
            </div>
          </Spin>
        )
      );
    }
    return (
      userInfo && (
        <Spin spinning={loading}>
          <div className="userInfoBox">
            <div className="firstBox">
              <div className="leftIcon">
                <AccountFilled />
              </div>
              <div className="rightInfo">
                <div className="id">
                  <Typography
                    level="h3a"
                    style={{ fontSize: '18px', lineHeight: '24px' }}
                  >
                    {userInfo.name}
                  </Typography>
                </div>
                <div className="id">
                  <Typography
                    level="body2"
                    style={{ color: '#BDBDBD', marginRight: '12px' }}
                  >
                    ID
                  </Typography>
                  <Typography level="body2">{userInfo.display_id}</Typography>
                </div>
                {userInfo?.email && (
                  <div className="id">
                    <EmailOutlined
                      style={{ color: '#BDBDBD', marginRight: '8px' }}
                    />
                    <Typography level="body2">{`${email?.replace(
                      email?.substring(1, 3),
                      '*',
                    )}@${emailSuffix}`}</Typography>
                  </div>
                )}
                <div className="id">
                  <PhoneOutlined
                    style={{ color: '#BDBDBD', marginRight: '8px' }}
                  />
                  <Typography level="body2">{`+84 ****${userInfo.phone_number?.substring(
                    4,
                  )}`}</Typography>
                </div>
                {userInfo?.referral_code && (
                  <div className="id">
                    <IglooOutlined
                      style={{ color: '#BDBDBD', marginRight: '8px' }}
                    />
                    <Typography level="body2">
                      {userInfo?.referral_code}
                    </Typography>
                    <a
                      className="copy"
                      onClick={() => {
                        copy(userInfo?.referral_code);
                        message.success(
                          formatMessage({
                            id: `Referral Code is Copied!`,
                          }),
                        );
                      }}
                    >
                      <Typography level="body2">
                        {formatMessage({ id: 'Copy' })}
                      </Typography>
                    </a>
                  </div>
                )}
                {/* <div style={{marginLeft: '4px'}}>
                    <a
                      onClick={() => {history.push('/me/profile')}}
                    >
                      <Typography level="body2">
                        {formatMessage({ id: 'View Profile' })}
                      </Typography>
                    </a>
                  </div> */}
              </div>
            </div>
            <Menu>
              <Menu.ItemGroup
                title={
                  <Typography level="body1" style={{ color: '#212121' }}>
                    {formatMessage({ id: 'Business' })}
                  </Typography>
                }
              >
                <Menu.Item
                  key="0"
                  onClick={() => handleMenuClick('/me/business-management')}
                  style={{ marginBottom: '24px' }}
                >
                  <section className="menu-item-container">
                    <Typography level="body1" style={{ color: '#666' }}>
                      {formatMessage({ id: 'Business Management' })}
                    </Typography>
                  </section>
                </Menu.Item>
              </Menu.ItemGroup>
              <Menu.ItemGroup
                title={
                  <Typography level="body1" style={{ color: '#212121' }}>
                    {formatMessage({ id: 'Payment' })}
                  </Typography>
                }
              >
                <Menu.Item
                  key="1"
                  onClick={() => handleMenuClick('/me/receiving-account')}
                  style={{ marginBottom: '24px' }}
                >
                  <section className="menu-item-container">
                    <Typography level="body1" style={{ color: '#666' }}>
                      {formatMessage({ id: 'Default Receiving Account' })}
                    </Typography>
                  </section>
                </Menu.Item>
                <Menu.Item
                  key="2"
                  onClick={() =>
                    handleMenuClick(
                      '/me/verify-payment-password/change-payment-password',
                    )
                  }
                  style={{ marginBottom: '24px' }}
                >
                  <section className="menu-item-container">
                    <Typography level="body1" style={{ color: '#666' }}>
                      {formatMessage({ id: 'Change Payment Password' })}
                    </Typography>
                  </section>
                </Menu.Item>
                <Menu.Item
                  key="8"
                  onClick={() => handleMenuClick('/me/forget-payment-password')}
                  style={{ marginBottom: '24px' }}
                >
                  <section className="menu-item-container">
                    <Typography level="body1" style={{ color: '#666' }}>
                      {formatMessage({ id: 'Forgot Payment Password' })}
                    </Typography>
                  </section>
                </Menu.Item>
              </Menu.ItemGroup>
              <Menu.ItemGroup
                title={
                  <Typography level="body1" style={{ color: '#212121' }}>
                    {formatMessage({ id: 'Password' })}
                  </Typography>
                }
              >
                <Menu.Item
                  key="3"
                  onClick={() => handleMenuClick('/me/change-password')}
                  style={{ marginBottom: '24px' }}
                >
                  <section className="menu-item-container">
                    <Typography level="body1" style={{ color: '#666' }}>
                      {formatMessage({ id: 'Change Password' })}
                    </Typography>
                  </section>
                </Menu.Item>
                <Menu.Item
                  key="4"
                  onClick={() => handleMenuClick('/me/forget-password')}
                  style={{ marginBottom: '24px' }}
                >
                  <section className="menu-item-container">
                    <Typography level="body1" style={{ color: '#666' }}>
                      {formatMessage({ id: 'Forgot Password' })}
                    </Typography>
                  </section>
                </Menu.Item>
              </Menu.ItemGroup>
              <Menu.Item key="5" onClick={handleLogout}>
                <section className="menu-item-container">
                  <Typography level="body1" style={{ color: '#212121' }}>
                    {formatMessage({ id: 'Logout' })}
                  </Typography>
                </section>
              </Menu.Item>
            </Menu>
          </div>
        </Spin>
      )
    );
  }, [userInfo, loading, localStorage.getItem('selected_lang')]);

  return <div>{userInfoRender}</div>;
};

export default MePage;
